import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/layout/Layout';
import { useLanguage } from '../contexts/LanguageContext';
import { getPendingTimesheets, bulkApproveTimesheets, bulkRejectTimesheets } from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  CheckCircle,
  XCircle,
  Clock,
  ArrowClockwise,
  Funnel,
  CheckSquare,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function TimesheetApprovalPage() {
  const { t, isSpanish } = useLanguage();
  const [pendingTimesheets, setPendingTimesheets] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterEmployee, setFilterEmployee] = useState('all');
  const [filterProject, setFilterProject] = useState('all');

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const loadPendingTimesheets = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await getPendingTimesheets();
      setPendingTimesheets(data);
    } catch (error) {
      toast.error(isSpanish ? 'Error al cargar timesheets' : 'Error loading timesheets');
    } finally {
      setLoading(false);
    }
  }, [isSpanish]);

  useEffect(() => {
    loadPendingTimesheets();
  }, [loadPendingTimesheets]);

  const handleBulkApprove = async () => {
    if (selectedIds.length === 0) {
      toast.error(isSpanish ? 'Selecciona al menos un timesheet' : 'Select at least one timesheet');
      return;
    }
    try {
      await bulkApproveTimesheets(selectedIds);
      toast.success(isSpanish ? `${selectedIds.length} timesheets aprobados` : `${selectedIds.length} timesheets approved`);
      setSelectedIds([]);
      loadPendingTimesheets();
    } catch (error) {
      toast.error(isSpanish ? 'Error al aprobar timesheets' : 'Error approving timesheets');
    }
  };

  const handleSingleApprove = async (id) => {
    try {
      await bulkApproveTimesheets([id]);
      toast.success(isSpanish ? 'Timesheet aprobado' : 'Timesheet approved');
      setSelectedIds(prev => prev.filter(x => x !== id));
      loadPendingTimesheets();
    } catch (error) {
      toast.error(isSpanish ? 'Error al aprobar' : 'Error approving');
    }
  };

  const handleReject = async (id) => {
    const reason = window.prompt(isSpanish ? 'Razon del rechazo:' : 'Rejection reason:');
    if (!reason) return;
    try {
      await bulkRejectTimesheets([id], reason);
      toast.success(isSpanish ? 'Timesheet rechazado' : 'Timesheet rejected');
      setSelectedIds(prev => prev.filter(x => x !== id));
      loadPendingTimesheets();
    } catch (error) {
      toast.error(isSpanish ? 'Error al rechazar' : 'Error rejecting');
    }
  };

  const handleBulkReject = async () => {
    if (selectedIds.length === 0) {
      toast.error(isSpanish ? 'Selecciona al menos un timesheet' : 'Select at least one timesheet');
      return;
    }
    const reason = window.prompt(isSpanish ? 'Razon del rechazo:' : 'Rejection reason:');
    if (!reason) return;
    try {
      await bulkRejectTimesheets(selectedIds, reason);
      toast.success(isSpanish ? `${selectedIds.length} timesheets rechazados` : `${selectedIds.length} timesheets rejected`);
      setSelectedIds([]);
      loadPendingTimesheets();
    } catch (error) {
      toast.error(isSpanish ? 'Error al rechazar' : 'Error rejecting');
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const employees = [...new Set(pendingTimesheets.map(ts => ts.employee_name).filter(Boolean))];
  const projects = [...new Set(pendingTimesheets.map(ts => ts.project_name).filter(Boolean))];

  const filteredTimesheets = pendingTimesheets.filter(ts => {
    const matchesEmployee = filterEmployee === 'all' || ts.employee_name === filterEmployee;
    const matchesProject = filterProject === 'all' || ts.project_name === filterProject;
    return matchesEmployee && matchesProject;
  });

  const totalHours = filteredTimesheets.reduce((sum, ts) => sum + (ts.hours || 0), 0);
  const billableHours = filteredTimesheets.filter(ts => ts.billing_category === 'facturable').reduce((sum, ts) => sum + (ts.hours || 0), 0);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E2B05E]"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="timesheet-approval-page">
        <div className="flex flex-col sm:flex-row gap-4 justify-between">
          <div>
            <h1 className="font-['Outfit'] text-3xl font-semibold text-[#F3F4F6]">
              {isSpanish ? 'Aprobacion de Timesheets' : 'Timesheet Approval'}
            </h1>
            <p className="text-[#9CA3AF] mt-1">
              {filteredTimesheets.length} {isSpanish ? 'timesheets pendientes' : 'timesheets pending'}
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={loadPendingTimesheets} className="border-[#242E42] text-[#9CA3AF]">
              <ArrowClockwise className="w-4 h-4 mr-2" />
              {isSpanish ? 'Refrescar' : 'Refresh'}
            </Button>
            {selectedIds.length > 0 && (
              <>
                <Button onClick={handleBulkReject} className="bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/30">
                  <XCircle className="w-5 h-5 mr-2" />
                  {isSpanish ? 'Rechazar' : 'Reject'} ({selectedIds.length})
                </Button>
                <Button onClick={handleBulkApprove} className="bg-emerald-500 hover:bg-emerald-600 text-white">
                  <CheckCircle className="w-5 h-5 mr-2" />
                  {isSpanish ? 'Aprobar' : 'Approve'} ({selectedIds.length})
                </Button>
              </>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="valhalla-card p-4">
            <p className="text-sm text-[#9CA3AF]">{isSpanish ? 'Total Pendientes' : 'Total Pending'}</p>
            <p className="text-2xl font-bold text-[#F3F4F6]">{filteredTimesheets.length}</p>
          </div>
          <div className="valhalla-card p-4">
            <p className="text-sm text-[#9CA3AF]">{isSpanish ? 'Total Horas' : 'Total Hours'}</p>
            <p className="text-2xl font-bold text-[#E2B05E]">{totalHours.toFixed(1)}h</p>
          </div>
          <div className="valhalla-card p-4">
            <p className="text-sm text-[#9CA3AF]">{isSpanish ? 'Horas Facturables' : 'Billable Hours'}</p>
            <p className="text-2xl font-bold text-emerald-400">{billableHours.toFixed(1)}h</p>
          </div>
          <div className="valhalla-card p-4">
            <p className="text-sm text-[#9CA3AF]">{isSpanish ? 'Seleccionados' : 'Selected'}</p>
            <p className="text-2xl font-bold text-[#3AB8D6]">{selectedIds.length}</p>
          </div>
        </div>

        <div className="flex gap-3 items-center">
          <Funnel className="w-4 h-4 text-[#9CA3AF]" />
          <Select value={filterEmployee} onValueChange={setFilterEmployee}>
            <SelectTrigger className="w-48 valhalla-input">
              <SelectValue placeholder={isSpanish ? 'Empleado' : 'Employee'} />
            </SelectTrigger>
            <SelectContent className="bg-[#151B26] border-[#242E42]">
              <SelectItem value="all">{isSpanish ? 'Todos los empleados' : 'All employees'}</SelectItem>
              {employees.map(emp => (
                <SelectItem key={emp} value={emp}>{emp}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterProject} onValueChange={setFilterProject}>
            <SelectTrigger className="w-48 valhalla-input">
              <SelectValue placeholder={isSpanish ? 'Proyecto' : 'Project'} />
            </SelectTrigger>
            <SelectContent className="bg-[#151B26] border-[#242E42]">
              <SelectItem value="all">{isSpanish ? 'Todos los proyectos' : 'All projects'}</SelectItem>
              {projects.map(proj => (
                <SelectItem key={proj} value={proj}>{proj}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {filteredTimesheets.length === 0 ? (
          <div className="valhalla-card p-12 text-center">
            <CheckSquare className="w-12 h-12 mx-auto text-emerald-400 mb-4" />
            <p className="text-[#F3F4F6] font-semibold text-lg">
              {isSpanish ? 'No hay timesheets pendientes' : 'No pending timesheets'}
            </p>
            <p className="text-[#9CA3AF] mt-1">
              {isSpanish ? 'Todos los timesheets han sido procesados' : 'All timesheets have been processed'}
            </p>
          </div>
        ) : (
          <div className="valhalla-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#242E42]">
                    <th className="p-3 text-left w-12">
                      <Checkbox
                        checked={selectedIds.length === filteredTimesheets.length && filteredTimesheets.length > 0}
                        onCheckedChange={(checked) => {
                          setSelectedIds(checked ? filteredTimesheets.map(t => t._id) : []);
                        }}
                      />
                    </th>
                    <th className="p-3 text-left text-sm font-medium text-[#9CA3AF]">{isSpanish ? 'Empleado' : 'Employee'}</th>
                    <th className="p-3 text-left text-sm font-medium text-[#9CA3AF]">{isSpanish ? 'Proyecto' : 'Project'}</th>
                    <th className="p-3 text-left text-sm font-medium text-[#9CA3AF]">{isSpanish ? 'Fecha' : 'Date'}</th>
                    <th className="p-3 text-left text-sm font-medium text-[#9CA3AF]">{isSpanish ? 'Horas' : 'Hours'}</th>
                    <th className="p-3 text-left text-sm font-medium text-[#9CA3AF]">{isSpanish ? 'Categoria' : 'Category'}</th>
                    <th className="p-3 text-left text-sm font-medium text-[#9CA3AF]">{isSpanish ? 'Estado' : 'Status'}</th>
                    <th className="p-3 text-left text-sm font-medium text-[#9CA3AF]">{isSpanish ? 'Acciones' : 'Actions'}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTimesheets.map((ts) => (
                    <tr key={ts._id} className="border-b border-[#242E42]/50 hover:bg-[#1E2636]/50 transition-colors">
                      <td className="p-3">
                        <Checkbox
                          checked={selectedIds.includes(ts._id)}
                          onCheckedChange={() => toggleSelect(ts._id)}
                        />
                      </td>
                      <td className="p-3 text-[#F3F4F6] font-medium">{ts.employee_name || 'N/A'}</td>
                      <td className="p-3 text-[#9CA3AF]">{ts.project_name || 'N/A'}</td>
                      <td className="p-3 text-[#9CA3AF]">{ts.date ? new Date(ts.date).toLocaleDateString('es-ES') : 'N/A'}</td>
                      <td className="p-3 font-semibold text-[#E2B05E]">{ts.hours || 0}h</td>
                      <td className="p-3">
                        <Badge className={ts.billing_category === 'facturable' ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' : 'bg-gray-500/20 text-gray-400 border-gray-500/30'}>
                          {ts.billing_category || 'N/A'}
                        </Badge>
                      </td>
                      <td className="p-3">
                        <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">
                          <Clock className="w-3 h-3 mr-1" />
                          {isSpanish ? 'Pendiente' : 'Pending'}
                        </Badge>
                      </td>
                      <td className="p-3">
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost" onClick={() => handleSingleApprove(ts._id)} className="text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10" title={isSpanish ? 'Aprobar' : 'Approve'}>
                            <CheckCircle className="w-5 h-5" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handleReject(ts._id)} className="text-red-400 hover:text-red-300 hover:bg-red-500/10" title={isSpanish ? 'Rechazar' : 'Reject'}>
                            <XCircle className="w-5 h-5" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
